import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Button, Pagination, SelectPageSize } from "nlib/ui";
import { checkAuditFetching, getAuditDataCount, getAuditSimilarPayeesData } from "selectors/audit";
import { getCurrentXeroOrganizationId } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import Arrays from "utils/Arrays";
import AuditActions from "actions/AuditActions";
import Constants from "const/Constants";
import MergeModal from "./lib/MergeModal";
import React, { useCallback, useEffect } from "react";
import SubTable from "./lib/SubTable";
import Table, { TableCell, TableHead, TableRow } from "nlib/ui/Table";
import Texts from "utils/Texts";
import useEnvVars from "hooks/useEnvVars";
import useShowCommonModal from "hooks/useShowCommonModal";
import useShowModal from "hooks/useShowModal";

const { TABLE_PAGE_SIZE } = Constants;

const SimilarPayeesTable = ({ children, disabled, currentRoute, refetchData }) => {
  const dispatch = useDispatch();

  const [
    mergeModalShown,
    showMergeModal,
    onCloseMergeModal
  ] = useShowModal();

  const showCommonModal = useShowCommonModal();

  const [{ page, pageSize = TABLE_PAGE_SIZE }, setEnvVars] = useEnvVars();

  const { uiTexts, messages } = useSelector(getTextsData);

  const data = useSelector(getAuditSimilarPayeesData);

  const count = useSelector(getAuditDataCount);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const auditFetchings = useSelector(checkAuditFetching);

  const handlePageChange = useCallback((value) => {
    setEnvVars({ page: value });
  }, [setEnvVars]);

  const handlePageSizeChange = useCallback((value) => {
    setEnvVars({ pageSize: value });
  }, [setEnvVars]);

  const handleMergeClick = useCallback(async(event, id) => {
    const { items = [] } = Arrays.findById(data, id, {});

    const modalResult = await showMergeModal(items);

    if (modalResult) {
      await dispatch(AuditActions.unusualBulkUpdate({
        currentRoute,
        filters: { vendorId: items.map((item) => item.id).filter((item) => item !== modalResult.id) },
        updateData: { address: modalResult, vendorId: modalResult.id }
      }));
      refetchData();
    }
  }, [currentRoute, data, dispatch, refetchData, showMergeModal]);

  const handleMarkAllAsCorrectClick = useCallback(async(event, id) => {
    const { items = [] } = Arrays.findById(data, id, {});

    if (items.length) {
      const result = await showCommonModal({
        text: Texts.replaceVars(messages.bulkTransactionsMarkAsCorrectConfirm, {
          transactionsCount: items.reduce((aggregator, { transactionsCount }) => aggregator + transactionsCount, 0)
        }),
        confirm: true
      });

      if (result) {
        await dispatch(AuditActions.unusualMarkAsRevised({
          currentRoute,
          filters: { vendorId: items.map((item) => item.id) }
        }));
        refetchData();
      }
    }
  }, [currentRoute, data, dispatch, messages, refetchData, showCommonModal]);

  useEffect(() => {
    const scrollContainer = document.querySelector("#scroll-container");

    if (scrollContainer) scrollContainer.scrollTo(0, 0);
  }, [page]);

  return (
    <>
      <div className={Css.unusualVendorCategoriesTable}>
        <div className={Css.content}>
          {children}
          <div className={Css.tableWrap}>
            <Table className={Css.table}>
              <TableRow>
                <TableHead>{uiTexts.payee}</TableHead>
              </TableRow>
              {data.map(({ id, items }) => {
                const totalCount = items.reduce((aggregator, { transactionsCount }) => aggregator + transactionsCount, 0);

                const [{ vendor: { name: firstVendorName } = {} } = {}] = items;

                return (
                  <TableRow key={id} className={Css.tableRow}>
                    <TableCell className={Css.tableCell}>
                      <div className={Css.itemContainer}>
                        <div className={Css.header}>
                          <div className={Css.title} title={firstVendorName}>
                            <Icons.User />
                            <span>{firstVendorName}</span>
                          </div>
                          <div className={Css.actions}>
                            {!xeroBusiness && (
                              <Button
                                outline primary
                                disabled={auditFetchings}
                                value={id}
                                className={Css.mergeButton}
                                icon={Icons.UniteSquare}
                                onClick={handleMergeClick}>
                                {uiTexts.mergeDuplicates}
                              </Button>
                            )}
                            <Button
                              outline success
                              disabled={auditFetchings}
                              value={id}
                              icon={Icons.ThumbsUp}
                              onClick={handleMarkAllAsCorrectClick}>
                              {uiTexts.markAllAsCorrect}
                            </Button>
                          </div>
                        </div>
                        <div className={Css.subTable}>
                          {items.map(({ id: vendorId, vendor, transactionsCount }) => (
                            <SubTable
                              key={vendorId}
                              disabled={disabled}
                              currentRoute={currentRoute}
                              vendor={vendor}
                              transactionsCount={transactionsCount}
                              totalCount={totalCount}
                              refetchAuditData={refetchData} />
                          ))}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </Table>
            <div className={Css.footer}>
              {count > +pageSize && (
                <Pagination
                  className={Css.pagination}
                  count={count}
                  page={+page}
                  pageSize={+pageSize}
                  onChange={handlePageChange} />
              )}
              <SelectPageSize
                pageSize={+pageSize}
                onChange={handlePageSizeChange} />
            </div>
          </div>
        </div>
      </div>
      {mergeModalShown && (
        <MergeModal items={mergeModalShown} onClose={onCloseMergeModal} />
      )}
    </>
  );
};

export default React.memo(SimilarPayeesTable);
